import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DateInput } from '@/components/ui/DateInput'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { CarrierPicker } from '../../../../../components/CarrierPicker'
import { useVendors } from '../../../../../hooks/useVendors'
import { apiErrorDetail } from '../../../../../lib/apiError'
import { CacheKeys } from '../../../../../lib/cache'
import { parseDateOnly } from '../../../../../lib/formatters'
import { toSnakeCase } from '../../../../../lib/typeHelper'
import { formatCurrency } from '../../../../../lib/utils'
import { CARRIER_NIRVANA_ENABLED, useFeatureFlag } from '../../../../../models/flag'
import { InsuranceIndication } from '../../../../../models/insuranceIndication'
import { InsuranceIndicationStatus, InsuranceOfferKind, Vendor } from '../../../../../models/insuranceTypes'
import { CoveragesFieldset } from './CoveragesFieldset'
import {
  computeTotalPremium,
  type CoverageFormData,
  coverageToFormData,
  type FeeFormData,
  feeToFormData,
  serializeCoverages,
  serializeFees,
  STAFF_COVERAGE_TYPE_OPTIONS,
} from './insuranceFormShared'
import { OneTimeFeesFieldset } from './OneTimeFeesFieldset'

/** Seed values for create mode (e.g. from a parsed policy document). */
export interface IndicationPrefill {
  vendor: Vendor | null
  vendorId: string
  carrierId?: string | null
  /** Carrier name read off the document that matched no carrier record, if any. */
  unmatchedCarrierName?: string | null
  coverages: {
    coverageType: string
    premium: number | null
    limit: number | null
    deductible: number | null
    comprehensiveDeductible?: number | null
    premiumPerUnit: number | null
  }[]
  oneTimeFees: { label: string; amount: number }[]
  sourceDocumentId?: string
  /** What the parse couldn't pin down, e.g. premiums that don't add up to the document's total. */
  warnings?: string[]
}

interface IndicationFormDialogProps {
  indication?: InsuranceIndication
  initialData?: IndicationPrefill
  /** Create mode only — editing always targets the indication itself. */
  kind?: InsuranceOfferKind
  orgId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface SaveRefusal {
  response?: { status?: number; data?: { error?: unknown; warnings?: unknown } }
}

export function IndicationFormDialog({
  indication,
  initialData,
  kind = InsuranceOfferKind.INDICATION,
  orgId,
  open,
  onOpenChange,
}: IndicationFormDialogProps) {
  const queryClient = useQueryClient()
  const { vendors } = useVendors()
  const isEdit = !!indication
  const isQuote = !isEdit && kind === InsuranceOfferKind.QUOTE
  // Editing a non-generated (pending/unavailable/declined/errored) indication
  // offers both a plain "Save" (keeps the status) and "Save and enable" (flips
  // it to GENERATED).
  const isEnableMode = isEdit && indication?.status !== InsuranceIndicationStatus.GENERATED

  const initialVendor = indication?.vendor ?? initialData?.vendor ?? ''
  const [vendor, setVendor] = useState<Vendor | ''>(initialVendor)
  // While Nirvana is turned off it is offered only on a row that already names it.
  const nirvanaEnabled = useFeatureFlag(CARRIER_NIRVANA_ENABLED)
  const vendorOptions = vendors.filter(
    (opt) => opt.value !== Vendor.NIRVANA || nirvanaEnabled || initialVendor === Vendor.NIRVANA
  )
  const [vendorId, setVendorId] = useState<string>(indication?.vendorId ?? initialData?.vendorId ?? '')
  const [carrierId, setCarrierId] = useState<string | null>(
    indication?.carrierId ?? initialData?.carrierId ?? null
  )
  const [brokerNote, setBrokerNote] = useState(indication?.brokerNote ?? '')
  const [pricedStart, setPricedStart] = useState<Date | undefined>(parseDateOnly(indication?.pricedStartDate))
  const pricedStartDate = pricedStart ? format(pricedStart, 'yyyy-MM-dd') : null
  const [brokerRecommended, setBrokerRecommended] = useState(indication?.brokerRecommended ?? false)
  const [oneTimeFees, setOneTimeFees] = useState<FeeFormData[]>(
    indication
      ? indication.oneTimeFees.map(feeToFormData)
      : (initialData?.oneTimeFees ?? []).map((f) => ({
          isNew: true,
          label: f.label,
          amount: String(f.amount),
        }))
  )
  const [deletedOneTimeFeeIds, setDeletedOneTimeFeeIds] = useState<string[]>([])
  const [coverages, setCoverages] = useState<CoverageFormData[]>(
    indication
      ? indication.coverages.map(coverageToFormData)
      : (initialData?.coverages ?? []).map((c) => ({
          isNew: true,
          coverageType: c.coverageType,
          vendorCoverageType: '',
          vendorPolicyId: '',
          premium: c.premium,
          limit: c.limit,
          deductible: c.deductible,
          comprehensiveDeductible: c.comprehensiveDeductible ?? null,
          premiumPerUnit: c.premiumPerUnit,
        }))
  )
  const [deletedCoverageIds, setDeletedCoverageIds] = useState<string[]>([])

  const newPremium = computeTotalPremium(coverages, oneTimeFees)
  const [saveError, setSaveError] = useState<string | null>(null)
  // What the server asked staff to confirm before it saves this edit.
  const [warnings, setWarnings] = useState<string[]>([])
  const [warnedEnable, setWarnedEnable] = useState(false)
  // A confirmation covers only the prices it was shown for.
  useEffect(() => setWarnings([]), [coverages, oneTimeFees, deletedCoverageIds, deletedOneTimeFeeIds])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'] })
    onOpenChange(false)
  }

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      if (isEdit) {
        return axios
          .patch(`/api/v1/insure/organisations/${orgId}/indications/${indication!.id}/`, toSnakeCase(data))
          .then((res) => res.data)
      }
      return axios
        .post(`/api/v1/insure/organisations/${orgId}/indications/`, toSnakeCase(data))
        .then((res) => res.data)
    },
    onSuccess: invalidate,
    onError: (error, payload) => {
      const refusal = (error as SaveRefusal).response
      if (refusal?.status === 409 && Array.isArray(refusal.data?.warnings)) {
        setWarnings(refusal.data.warnings.map(String))
        setWarnedEnable(payload.status === InsuranceIndicationStatus.GENERATED)
        return
      }
      const message = refusal?.data?.error
      setSaveError(
        typeof message === 'string'
          ? message
          : apiErrorDetail(error, 'Could not save these changes. Try again.')
      )
    },
  })

  function handleSave(enable: boolean, acknowledgeWarnings = false) {
    const payload: Record<string, unknown> = {
      vendor,
      vendorId: vendorId || null,
      carrierId,
      brokerNote,
      brokerRecommended,
      oneTimeFees: serializeFees(oneTimeFees),
      coverages: serializeCoverages(coverages),
    }
    // Sent only when changed: a carrier may record its own date while this form is open.
    if (pricedStartDate !== (indication?.pricedStartDate ?? null)) {
      payload.pricedStartDate = pricedStartDate
    }
    if (isEdit) {
      payload.deletedOneTimeFeeIds = deletedOneTimeFeeIds
      payload.deletedCoverageIds = deletedCoverageIds
      // The carrier total this form was opened with, so a re-priced quote refuses these prices.
      if (indication!.metadata.vendorTotalPremium != null) {
        payload.expectedVendorTotal = indication!.metadata.vendorTotalPremium
      }
    } else {
      if (initialData?.sourceDocumentId) {
        payload.sourceDocumentId = initialData.sourceDocumentId
      }
      payload.createQuote = isQuote
    }
    if (enable) {
      payload.status = InsuranceIndicationStatus.GENERATED
    }
    if (acknowledgeWarnings) {
      payload.acknowledgeWarnings = true
    }
    setSaveError(null)
    setWarnings([])
    mutation.mutate(payload)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Edit Indication - ${indication!.vendorName}`
              : isQuote
                ? 'Create quote'
                : 'Create indication'}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
          {initialData?.warnings?.map((warning) => (
            <Banner key={warning} variant="warning" small>
              {warning}
            </Banner>
          ))}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-xs">Vendor</Label>
              <Select value={vendor} onValueChange={(value) => setVendor(value as Vendor)}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue placeholder="Select vendor…" />
                </SelectTrigger>
                <SelectContent>
                  {vendorOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Vendor ID (optional)</Label>
              <Input
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="mt-1"
                placeholder="Policy number / vendor ID"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Carrier (optional)</Label>
              <div className="mt-1">
                <CarrierPicker value={carrierId} onChange={setCarrierId} />
              </div>
              {!carrierId && initialData?.unmatchedCarrierName && (
                <p className="text-muted-foreground mt-1 text-xs">
                  Document says “{initialData.unmatchedCarrierName}” — no carrier record matches, pick one or
                  add it under Carriers.
                </p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Policy start date (optional)</Label>
              <div className="mt-1">
                <DateInput
                  label="Start date the carrier priced"
                  value={pricedStart}
                  onChange={setPricedStart}
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-muted-foreground text-xs">Broker note (optional)</Label>
            <div className="mt-1">
              <Textarea
                value={brokerNote}
                onChange={(e) => setBrokerNote(e.target.value)}
                placeholder='Shown to the customer on their Quotes Page under "Notes from <broker>".'
              />
            </div>

            <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={brokerRecommended}
                onCheckedChange={(checked) => setBrokerRecommended(checked === true)}
              />
              Broker recommended
            </label>
          </div>

          {isEdit && (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                <span className="text-muted-foreground text-sm">Current premium</span>
                <span className="font-semibold">{formatCurrency(indication!.premium)}</span>
              </div>
              <div className="flex items-center justify-between rounded-md bg-blue-50 px-3 py-2">
                <span className="text-muted-foreground text-sm">New premium</span>
                <span className="font-semibold">{formatCurrency(newPremium)}</span>
              </div>
            </div>
          )}

          <OneTimeFeesFieldset
            value={oneTimeFees}
            onChange={setOneTimeFees}
            onRemoveExisting={(id) => setDeletedOneTimeFeeIds((prev) => [...prev, id])}
          />

          <CoveragesFieldset
            coverageOptions={STAFF_COVERAGE_TYPE_OPTIONS}
            value={coverages}
            onChange={setCoverages}
            onRemoveExisting={(id) => setDeletedCoverageIds((prev) => [...prev, id])}
          />
        </div>

        {saveError && (
          <Banner variant="error" small>
            {saveError}
          </Banner>
        )}
        {warnings.length > 0 && (
          <Banner variant="warning" small>
            {warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </Banner>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {warnings.length > 0 ? (
            <Button
              onClick={() => handleSave(warnedEnable, true)}
              disabled={mutation.isPending}
              loading={mutation.isPending ? 'Saving...' : undefined}
            >
              Save anyway
            </Button>
          ) : isEnableMode ? (
            <>
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                disabled={mutation.isPending || !vendor}
                loading={mutation.isPending ? 'Saving...' : undefined}
              >
                Save
              </Button>
              <Button
                onClick={() => handleSave(true)}
                disabled={mutation.isPending || !vendor}
                loading={mutation.isPending ? 'Saving...' : undefined}
              >
                Save and enable
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleSave(false)}
              disabled={mutation.isPending || !vendor}
              loading={mutation.isPending ? 'Saving...' : undefined}
            >
              {isEdit ? 'Save changes' : isQuote ? 'Create quote' : 'Create indication'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
